import { SkeletonColumn } from "./types";

// Game list skeleton — /all-games (renders all 6 columns incl. Last Modified).
export const gameListSkeletonConfig: SkeletonColumn[] = [
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Platforms", type: "platforms" },
  { label: "Last Modified", type: "date", width: "w-24" },
];

// /search renders the /releases-style stats table (createSearchColumns =
// createMonthlyColumns minus Conversion, plus Platforms). This config mirrors
// the DEFAULT-VISIBLE set — Peak CCU / Peak Sellers / Platforms are hidden by
// default (SEARCH_DEFAULT_HIDDEN_COLUMNS); keep the three lists in sync.
export const searchGameListSkeletonConfig: SkeletonColumn[] = [
  { label: "#", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Current CCU", type: "number", width: "w-16" },
  { label: "Sellers Rank", type: "number", width: "w-16" },
  { label: "Followers", type: "number", width: "w-20" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Reviews", type: "text", width: "w-24" },
  { label: "1st Week", type: "number", width: "w-16" },
];

// Configuration for top sellers skeleton (with rank)
export const topSellerListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Platforms", type: "platforms" },
];

// Configuration for top followers skeleton (with followers count and changes)
export const topFollowersListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-14" },
  { label: "Followers", type: "number", width: "w-28" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Platforms", type: "platforms" },
  { label: "1D", type: "number", width: "w-16" },
  { label: "7D", type: "number", width: "w-16" },
  { label: "30D", type: "number", width: "w-16" },
];

// Configuration for top wishlist skeleton (with rank and changes)
export const topWishlistListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Platforms", type: "platforms" },
  { label: "1D", type: "number", width: "w-16" },
  { label: "7D", type: "number", width: "w-16" },
  { label: "30D", type: "number", width: "w-16" },
];

// Configuration for top CCU skeleton (with rank and online count)
export const topCCUListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-14" },
  { label: "Online", type: "number", width: "w-24" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Platforms", type: "platforms" },
];

// Configuration for /releases (monthly) — matches createMonthlyColumns()'s 13
// default-visible columns in order (the table has a column-visibility toggle,
// but nothing is hidden by default).
export const monthlyListSkeletonConfig: SkeletonColumn[] = [
  { label: "#", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Conversion", type: "number", width: "w-16" },
  { label: "Current CCU", type: "number", width: "w-16" },
  { label: "Peak CCU", type: "number", width: "w-16" },
  { label: "Sellers Rank", type: "number", width: "w-16" },
  { label: "Peak Sellers", type: "number", width: "w-16" },
  { label: "Followers", type: "number", width: "w-20" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "Reviews", type: "text", width: "w-24" },
  { label: "1st Week", type: "number", width: "w-16" },
];

// Configuration for /upcoming-releases (exact release time + followers + price)
// The Release column renders as two stacked rows in the real table:
//   "Aug 27, 2026, 14:45 (Aug 27, 2026, 11:45 UTC)"  ← wide
//   "in 99d"                                          ← narrow
// Use customRender to match that visually instead of a single date bar.
export const upcomingListSkeletonConfig: SkeletonColumn[] = [
  { label: "#", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  {
    label: "Release",
    type: "custom",
    customRender: () => (
      <div className="flex flex-col gap-1">
        <div className="h-3 w-52 bg-[#1b2838] rounded animate-pulse" />
        <div className="h-2.5 w-14 bg-[#1b2838] rounded animate-pulse" />
      </div>
    ),
  },
  { label: "Followers", type: "number", width: "w-16" },
  { label: "Price", type: "number", width: "w-12" },
];

// Configuration for /prerelease — createUpcomingColumns({hideExactTime, withHype})
// adds Hype + 7d Followers between Release and Followers, and the Release cell is
// DAY-ONLY ("Jun 22, 2026" + "· in 12d") rather than the wide exact-time line.
export const preReleaseListSkeletonConfig: SkeletonColumn[] = [
  { label: "#", type: "number", width: "w-14" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  {
    label: "Release",
    type: "custom",
    customRender: () => (
      <div className="flex flex-col gap-1">
        <div className="h-3 w-28 bg-[#1b2838] rounded animate-pulse" />
        <div className="h-2.5 w-12 bg-[#1b2838] rounded animate-pulse" />
      </div>
    ),
  },
  { label: "Hype", type: "number", width: "w-16" },
  { label: "7d Followers", type: "number", width: "w-16" },
  { label: "Followers", type: "number", width: "w-16" },
  { label: "Price", type: "number", width: "w-12" },
];

// Configuration for More Like This skeleton
export const topMltListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-14" },
  { label: "Mentions", type: "number", width: "w-28" },
  { label: "Image", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Release", type: "date", width: "w-24" },
  { label: "Price", type: "number", width: "w-16" },
  { label: "1D", type: "number", width: "w-16" },
  { label: "7D", type: "number", width: "w-16" },
  { label: "30D", type: "number", width: "w-16" },
];

// Configuration for /publishers list — Rank / Avatar / Name / Followers /
// Total Players / Apps / In Top Sellers (publisherColumns).
export const publisherListSkeletonConfig: SkeletonColumn[] = [
  { label: "Rank", type: "number", width: "w-16" },
  { label: "Avatar", type: "image", noPadding: true },
  { label: "Name", type: "text", width: "w-48" },
  { label: "Followers", type: "number", width: "w-28" },
  { label: "Total Players", type: "number", width: "w-28" },
  { label: "Apps", type: "number", width: "w-20" },
  { label: "In Top Sellers", type: "number", width: "w-28" },
];
